import React, { useState } from 'react';
import { X } from 'lucide-react';

// Simbologia real do OpenTopoMap (der-stefan/OpenTopoMap, styles-otm/roads-*.xml)
// casing = contorno preto por baixo; fill = traço por cima
const SECTIONS = [
  {
    title: 'Caminhos de terra · dificuldade (tracktype)',
    note: 'Quanto mais fina e mais tracejada a linha, pior o piso.',
    rows: [
      { casing: '#000', fill: '#fff', w: 6, label: 'Grau 1 — linha dupla (preto + branco)', desc: 'Piso firme, terra batida ou gravilha compacta · qualquer 4×4 ou SUV' },
      { stroke: '#000', w: 3, label: 'Grau 2 / sem grau — preto contínuo grosso', desc: 'Terra solta, pedra miúda · SUV robusto ou 4×4' },
      { stroke: '#000', w: 1.8, label: 'Grau 3 e 4 — preto contínuo fino', desc: 'Pedra, lama, sulcos · 4×4 com pneus AT, redutora no grau 4' },
      { stroke: '#000', w: 1.6, dash: '5,4', label: 'Grau 5 — preto tracejado', desc: 'Extremo · só 4×4 preparado e equipamento de recuperação' },
    ],
  },
  {
    title: 'Trilhos pedestres · dificuldade (escala SAC)',
    note: 'Trilhos pouco visíveis no terreno aparecem esbatidos.',
    rows: [
      { stroke: '#000', w: 1.6, dash: '1.5,3', label: 'T1–T2 — pontilhado regular', desc: 'Caminhada / caminhada de montanha' },
      { stroke: '#000', w: 1.6, dash: '1.5,3,1.5,3,1.5,8', label: 'T3–T4 — três pontos e intervalo', desc: 'Montanha exigente / alpino' },
      { stroke: '#000', w: 1.6, dash: '1.5,3,1.5,11', label: 'T5–T6 / via ferrata — dois pontos e intervalo longo', desc: 'Alpino difícil · equipamento de escalada' },
      { stroke: '#000', w: 1.6, dash: '1.5,3', opacity: 0.4, label: 'Trilho pouco visível — pontilhado esbatido', desc: 'trail_visibility bad/no · difícil de seguir no local' },
    ],
  },
  {
    title: 'Estradas',
    rows: [
      { casing: '#000', fill: '#f59729', w: 7, label: 'Auto-estrada / nacional', desc: 'Motorway · trunk · primary — laranja' },
      { casing: '#000', fill: '#f7e761', w: 6, label: 'Regional / municipal', desc: 'Secondary · tertiary — amarelo' },
      { casing: '#000', fill: '#fff', w: 5, label: 'Estrada local / rua / serviço', desc: 'Unclassified · residential · service — branco' },
      { stroke: '#aaa', w: 3, dash: '5,4', label: 'Em construção', desc: 'Cinzento tracejado' },
    ],
  },
  {
    title: 'Relevo, água e limites',
    rows: [
      { stroke: '#c45700', w: 1.4, label: 'Curvas de nível', desc: 'Castanho-alaranjado · mais grossa a cada 100 m' },
      { stroke: '#3c8fd6', w: 3, label: 'Rios, ribeiras e lagos', desc: 'Azul' },
      { stroke: '#8301b1', w: 1.6, dash: '6,3', label: 'Fronteira / limite administrativo', desc: 'Roxo · faixa clara nos limites de país' },
    ],
  },
  {
    title: 'Elementos da Tectur',
    rows: [
      { stroke: '#E91E63', w: 5, label: 'Rota', desc: 'Magenta com setas a indicar o sentido' },
      { dot: '#16A34A', text: 'A', label: 'Início da rota', desc: 'Balão verde' },
      { dot: '#DC2626', text: 'B', label: 'Fim da rota', desc: 'Balão vermelho' },
      { dot: '#8B4513', text: '★', label: 'Pontos de interesse da rota', desc: 'Toque para ver a informação' },
    ],
  },
];

const Swatch = ({ r }) => {
  if (r.dot) {
    return (
      <span className="w-11 h-6 flex items-center justify-center shrink-0">
        <span className="w-5 h-5 rounded-full text-[10px] font-black text-white flex items-center justify-center border-2 border-white shadow" style={{ background: r.dot }}>{r.text}</span>
      </span>
    );
  }
  return (
    <svg width="44" height="24" className="shrink-0 rounded bg-[#f4f1e6] border border-slate-200">
      {r.casing && <line x1="3" y1="12" x2="41" y2="12" stroke={r.casing} strokeWidth={r.w} />}
      <line x1="3" y1="12" x2="41" y2="12" stroke={r.fill || r.stroke} strokeWidth={r.casing ? r.w - 2.5 : r.w}
        strokeDasharray={r.dash} strokeOpacity={r.opacity ?? 1} strokeLinecap={r.dash && r.dash.startsWith('1.5') ? 'round' : 'butt'} />
    </svg>
  );
};

export const WebMapLegendPanel = ({ onClose }) => (
  <div className="absolute top-3 right-3 bottom-3 z-[700] w-[320px] max-w-[calc(100%-1.5rem)] bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col" data-testid="map-legend-panel">
    <div className="flex items-start justify-between gap-2 px-4 pt-3 pb-2 border-b border-slate-200">
      <div>
        <div className="text-sm font-black text-slate-900">Legenda do mapa</div>
        <div className="text-[11px] text-slate-500">Mapa base OpenTopoMap · com mais zoom passa a satélite</div>
      </div>
      <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-600" data-testid="map-legend-close-btn" aria-label="Fechar legenda"><X className="w-4 h-4" /></button>
    </div>
    <div className="overflow-y-auto px-4 py-2" data-testid="map-legend-scroll">
      {SECTIONS.map((s) => (
        <div key={s.title} className="mt-2 mb-3">
          <div className="text-[11px] font-black uppercase tracking-wide text-[#0D5373]">{s.title}</div>
          {s.note && <div className="text-[11px] italic text-slate-500 mb-1">{s.note}</div>}
          {s.rows.map((r) => (
            <div key={r.label} className="flex items-center gap-3 py-1.5" data-testid="map-legend-row">
              <Swatch r={r} />
              <div className="min-w-0">
                <div className="text-xs font-bold text-slate-900 leading-tight">{r.label}</div>
                <div className="text-[11px] text-slate-500 leading-snug">{r.desc}</div>
              </div>
            </div>
          ))}
        </div>
      ))}
      <div className="text-[10px] text-slate-400 pb-2">Dados © OpenStreetMap · estilo © OpenTopoMap (CC-BY-SA)</div>
    </div>
  </div>
);

export const WebMapLegendButton = ({ open, onToggle }) => (
  <button
    onClick={onToggle}
    className={`px-2.5 py-1.5 rounded-lg text-xs font-bold shadow-md border text-left transition-colors ${
      open ? 'bg-[#0D5373] text-white border-[#0D5373]' : 'bg-white/95 text-slate-700 border-slate-200 hover:border-slate-400'
    }`}
    data-testid="toggle-map-legend-btn"
    title="Ver o significado das linhas e cores do mapa"
  >
    🗺️ Legenda
  </button>
);

export const useLegendToggle = () => {
  const [open, setOpen] = useState(false);
  return [open, () => setOpen((v) => !v), () => setOpen(false)];
};
